<template>
  <div id="attribute-form">
    <Loading
      :active.sync="isLoading"
      :is-full-page="fullPage"
      :color="loaderColor"
      loader="dots"
      :background-color="loaderBackgroundColor"
    />
    <div v-if="showMultiLookupResults">
      <MultipleLookups
        :tableValues="lookupResults"
        @yesButtonClick="updateLookupValues"
        @noButtonClick="showMultiLookupResults = false"
      />
    </div>
    <div v-show="!showMultiLookupResults">
      <div id="header">
        <span id="header-text">Fields</span>
        <div id="due-date-wrapper" v-if="showDatePicker">
          <VasionInput
            v-if="!canModifyDueDate"
            id="due-date"
            v-model="dueDate"
            name="due-date"
            :title="dueDateLabel"
            inputType="top-white"
            :readonly="true"
          />
          <md-datepicker
            v-if="canModifyDueDate"
            id="due-date"
            v-model="dueDate"
            name="due-date"
            :md-disabled-dates="disabledDates"
          >
            <label>{{ dueDateLabel }}</label>
          </md-datepicker>
        </div>
      </div>

      <div v-show="!showUploadDialog" id="start-workflow-main-section">
        <IndexFieldList
          id="index-field-list"
          ref="indexFieldListComponent"
          :fields="indexFields"
          :selectorOnly="false"
          :showSubmit="false"
          :useMultiSelectLists="true"
          :enforceFieldSecurity="true"
          @fieldValueUpdated="updateLocalFieldValues"
          @isValid="markFieldAsValid"
          @notValid="markFieldAsNotValid"
        />
        <div v-if="uploadedFilesList.length > 0" class="uploaded-files-list">
          <div v-for="(file, index) in uploadedFilesList" :key="index" class="uploaded-file-item">
            <div>
              <span class="file-name"> {{ file.name }} </span><span class="bytes">({{ parseInt(file.size / 1000) }}K)</span>
            </div>
            <VasionButton
              :icon="'VasionExIcon'"
              title="Remove Uploaded File"
              class="uploaded-file-delete"
              @vasionButtonClicked="removeUploadedFile(index)"
            />
          </div>
        </div>
      </div>

      <div v-if="showUploadDialog" id="upload-new-file-dialog" class="dialog">
        <div id="drag-file-contents" class="upload-container">
          <div class="file-pond-block-wrapper">
            <file-pond
              ref="pond"
              allowMultiple="true"
              class="new-document"
              :server="serverOptions"
              :label-idle="filePondLabel"
              @processfile="processedFile"
              @addfilestart="fileAdded"
            />
          </div>
          <div class="md-layout">
            <div class="md-layout-item text-right">
              <VasionButton
                id="back-to-workflow"
                name="back"
                :classProp="'secondary'"
                @vasionButtonClicked="showUploadDialog = false"
              >
                Back
              </VasionButton>
            </div>
          </div>
        </div>
      </div>

      <div id="footer" v-if="!showUploadDialog">
        <label>
          {{ errorText }}
        </label>

        <VasionButton
          id="back"
          name="back"
          :classProp="'secondary'"
          @vasionButtonClicked="backToFileUpload"
        >
          Back
        </VasionButton>

        <VasionButton
          id="cancel"
          name="cancel"
          :classProp="'secondary'"
          class="button-right"
          @vasionButtonClicked="cancelForm"
        >
          Cancel
        </VasionButton>

        <VasionButton
          v-show="showLookupButton"
          id="dbLookup"
          name="dbLookup"
          :classProp="'secondary'"
          class="button-right"
          @vasionButtonClicked="runDBLookup"
        >
          Run Lookup
        </VasionButton>

        <VasionButton
          v-if="allowUploads"
          id="upload-file-button"
          name="upload-file-button"
          class="button-right"
          :classProp="'secondary'"
          @vasionButtonClicked="showUploadDialog = true"
        >
          Attach Files
        </VasionButton>

        <VasionButton
          id="start-workflow"
          name="start-workflow"
          :classProp="'primary'"
          :isDisabled="!formIsValid"
          class="button-right"
          @vasionButtonClicked="prepForStartWorkflow"
        >
          Start Workflow
        </VasionButton>
      </div>
    </div>
    <VasionSnackbar
      id="results-snack"
      :showSnackbarBool.sync="showSnackbar"
      :snackbarImage="snackbarImage"
      :snackbarSubTitle="snackbarSubTitle"
      :snackbarTitle="snackbarTitle"
    />

    <VasionAsynchronousConfirmDialog ref="confirm" confirmText="Yes" cancelText="No" />
  </div>
</template>

<script>
import Loading from 'vue-loading-overlay';
import IndexFieldList from '@/components/IndexFieldList.vue'
import MultipleLookups from '@/components/vault/MultipleLookups.vue'
import { loaderBackgroundColor, loaderColor } from '@/assets/js/styleConfig'
import VasionAsynchronousConfirmDialog from '@/components/shared/VasionAsynchronousConfirmDialog.vue'

export default {
  name: 'StartWorkflowAttributeForm',
  components: {
    IndexFieldList,
    Loading,
    MultipleLookups,
    VasionAsynchronousConfirmDialog,
  },
  props: {
    workflowID: {
      type: Number,
      default: 0,
      required: true,
    },
  },
  data: function () {
    return {
      allowUploads: false,
      canModifyDueDate: false,
      currentDate: new Date(),
      dueDate: null,
      dueDateLabel: '',
      disabledDates: date => {
        return date < this.currentDate
      },
      errorText: '',
      formIsValid: true,
      fullPage: true,
      indexFields: [],
      invalidFields: [],
      isLoading: false,
      loaderBackgroundColor: loaderBackgroundColor,
      loaderColor: loaderColor,
      localFieldValues: [],
      lookupResults: {},
      numFilesUploaded: 0,
      serverOptions: {
        process: this.processHandler,
      },
      snackbarImage: false,
      snackbarSubTitle: '',
      snackbarTitle: '',
      showDatePicker: false,
      showLookupButton: false,
      showMultiLookupResults: false,
      showSnackbar: false,
      showUploadDialog: false,
      uploadedFileIDs: [],
      uploadedFilesList: [],
      uploadedFilesPayloads: [],
      wfFolderId: null,
    }
  },
  computed: {
    filePondLabel() {
      return `<span class="filepond-drag">Drag your files here</span>
              <br/><br/>
              <span class="filepond-label-action">or click to upload your files</span>`
    },
    selectedDocumentId() { return this.$store.state.workflow.selectedDocumentId },
    startWorkflowPathData() { return this.$store.state.workflow.startWorkflowPathData },
    uploadedFile() { return this.$store.state.storage.uploadedFile },
  },
  mounted() {
    this.$root.confirm = this.$refs.confirm
  },
  async created() {
    this.$material.locale.dateFormat = 'MM/dd/yyyy'
    this.currentDate.setHours(0, 0, 0, 0)
    this.indexFields = await this.$store.dispatch('common/getIndexFieldsForForm', this.startWorkflowPathData.defaultAttributeFormID)
    this.indexFields.forEach((field, index) => {
      this.indexFields[index].readOnly = false
    })

    let result = await this.$store.dispatch('document/getDefaultLookupId', this.startWorkflowPathData.defaultAttributeFormID)
    if (result && result.Value) {
      this.showLookupButton = Number(result.Value) > 0
    }

    const workflowSettings = await this.$store.dispatch('workflow/getWorkflowSettingsNoCommit', this.workflowID)
    this.allowUploads = workflowSettings.supportAttachments
    this.canModifyDueDate = workflowSettings.canModifyDueDate

    const dueDateCalculation = await this.$store.dispatch('workflow/calculateDueDateAndSettings', this.workflowID)

    await this.$store.dispatch('storage/getVasionDriveFolderId')
    this.wfFolderId = this.$store.state.storage.vasionDriveFolderId

    this.dueDate = dueDateCalculation.dueDate
    this.dueDateLabel = dueDateCalculation.dueDateLabel
    if (this.dueDate !== null) this.showDatePicker = true
  },
  methods: {
    async backToFileUpload() {
      let res = await this.$root.confirm.open({
        message: 'Are you sure you want to leave and select another file? Your current data will be lost.',
      })
      if (res) {
        this.$emit('backButtonClick')
        return
      }
    },
    async cancelForm() {
      let res = await this.$root.confirm.open({
        message: 'Are you sure you want to cancel? Your current data will be lost.',
      })
      if (res) {
        this.$emit('cancelButtonClick')
        return
      }
    },
    fileAdded() {
      this.numFilesUploaded += 1
    },
    getLocalIndexFieldValuePairs() {
      if (!this.localFieldValues) {
        return []
      }
      
      return this.localFieldValues.map(e => {
        if (Object.prototype.toString.call(e.value) === '[object Array]') {
          const dropdownValues = []
          e.value.forEach(v => {
            if (v.name) {
              dropdownValues.push(v.name)
            }
            else {
              dropdownValues.push(v)
            }
          })
          const dropDownSingleString = dropdownValues.join(';')
          return {
            Key: e.name,
            Value: dropDownSingleString,
          }
        } else {
          return {
            Key: e.name,
            Value: e.value,
          }
        }
      })
    },
    getStringFromIndexValue(indexValue) {
      let text = ''

      if (!indexValue) {
        return text
      }

      if (Array.isArray(indexValue)) {
        // .value is actually a string array, so we got that going for us...
        indexValue.map((value) => {
          text += `${value};`
          return true
        })

        if (text !== '') {
          // remove the trailing semi-colon
          text = text.substring(0, text.length - 1)
        }
      } else {
        text = indexValue
      }

      return text
    },
    async linkDocuments(documentIDs) {
      if (!documentIDs || documentIDs.length <= 0) {
        return
      }

      const linkDocumentPayload = {
        BaseDocumentID: parseInt(this.selectedDocumentId, 10),
        LinkDocumentIDs: {
            Values: documentIDs,
        },
      }

      await this.$store.dispatch('document/linkDocuments', linkDocumentPayload)
    },
    markFieldAsNotValid(field) {
      if (this.invalidFields.includes(field.type)) return
      this.invalidFields.push(field.type)
    },
    markFieldAsValid(field) {
      const indexToBeDeleted = this.invalidFields.indexOf(field.type)
      if (indexToBeDeleted !== -1)
        this.invalidFields.splice(indexToBeDeleted, 1)
    },
    async prepForStartWorkflow() {
      if (this.showDatePicker && this.canModifyDueDate && this.dueDate && (new Date(this.dueDate) < this.currentDate)) {
        this.snackbarTitle = 'Workflow Due Date is invalid'
        this.snackbarSubTitle = 'The Workflow Due Date has already passed.'
        this.snackbarImage = false
        this.showSnackbar = true
        return
      }

      const requiredFieldMissing = this.indexFields.some(field => field.required && !this.localFieldValues.some(localField => localField.name === field.value))
      if (requiredFieldMissing) {
        this.snackbarTitle = 'Required fields missing'
        this.snackbarSubTitle = 'Please fill in all required fields to start the workflow.'
        this.snackbarImage = false
        this.showSnackbar = true
        return
      }

      if (this.invalidFields.length && this.invalidFields.includes(12)) {
        this.snackbarTitle = 'Field(s) error'
        this.snackbarSubTitle = 'Please enter a valid email address.'
        this.snackbarImage = false
        this.showSnackbar = true
        return
      }

      this.isLoading = true

      // upload the file and save if necessary
      if (this.selectedDocumentId === null || this.selectedDocumentId <= 0) {
        const tempDocID = await this.$store.dispatch('storage/uploadFileToVasion', this.uploadedFile)
        await this.$store.dispatch('workflow/setSelectedDocumentId', tempDocID)
      }

      if (this.selectedDocumentId === null || this.selectedDocumentId <= 0) {
        // we still don't have a document id, which is bad news :(
        this.$emit('workflowUnsuccessful')
        return
      }

      let uploadIDs = []
      if (this.uploadedFilesPayloads.length > 0) {
        const promises = []
        this.uploadedFilesPayloads.forEach(payload => {
          promises.push(this.$store.dispatch('storage/uploadFileToVasion', payload))
        })
        uploadIDs = await Promise.all(promises)
      }

      uploadIDs.forEach(file => {
        this.uploadedFileIDs.push(parseInt(file, 10))
      })

      if (this.uploadedFileIDs.length > 0) {
        this.linkDocuments(this.uploadedFileIDs)
      }

      // save the field data
      const success = await this.saveAttributeData()
      if (!success) {
        this.$emit('workflowUnsuccessful')
        return
      }

      // put the due date in the store so it can be used upstream
      await this.$store.dispatch('workflow/setWorkflowDueDate', this.dueDate)
      this.$emit('kickOffWorkflow')
    },
    processedFile() {
      this.numFilesUploaded -= 1
      if (this.numFilesUploaded <= 0) {
        this.showUploadDialog = false
      }
    },
    processHandler: async function (fieldName, file, metadata, load, error, progress, abort) {
      this.uploadedFilesList.push(file)
      const payload = {
        file: file,
        folderId: parseInt(this.wfFolderId, 10),
      }
      this.uploadedFilesPayloads.push(payload)

      progress(true, 0, 1024)
      load(file.name)

      return {
        abort: () => {
          abort();
        },
      };
    },
    removeUploadedFile(index) {
      this.uploadedFilesList.splice(index, 1)
      this.uploadedFilesPayloads.splice(index, 1)
    },
    async runDBLookup() {
      this.isLoading = true

      const FieldValues = {
        Values: this.getLocalIndexFieldValuePairs(),
      }
      this.initialAttributeFieldListSize = 0

      this.lookupResults = await this.$store.dispatch('attributeForm/attributeFieldLookUp', {
        IndexFormId: this.startWorkflowPathData.defaultAttributeFormID,
        FieldValues,
      })

      switch (this.lookupResults.Rows?.length) {
        case 0:
          this.errorText = 'No Results Found'
          // eslint-disable-next-line no-return-assign
          setTimeout(() => this.errorText = '', 3000);
          break;
        case 1:
          this.$refs.indexFieldListComponent.updateFieldsFromLookup(this.lookupResults)
          break
        default:
          this.showMultiLookupResults = true
          break
      }

      this.isLoading = false
    },
    async saveAttributeData() {
      if (!this.startWorkflowPathData.defaultAttributeFormID || this.startWorkflowPathData.defaultAttributeFormID <= 0) {
        return false
      }

      const payload = {
        DocumentId: this.selectedDocumentId,
        FormId: this.startWorkflowPathData.defaultAttributeFormID,
        FieldValues: {
          Values: this.getLocalIndexFieldValuePairs(),
        },
      }

      return this.$store.dispatch('document/saveDocumentAttributes', payload)
    },
    toggleStartWorkflowButtonDisabled(value) {
      if (value !== undefined && value !== null && typeof value === 'boolean') {
        this.formIsValid = value
      } else {
        this.formIsValid = !this.formIsValid
      }
    },
    updateLocalFieldValues(childComponentValues) {
      this.localFieldValues = childComponentValues
    },
    updateLookupValues(item) {
      this.$refs.indexFieldListComponent.updateFieldsFromLookup(item)
      this.showMultiLookupResults = false
    },
  },
}
</script>

<style lang="scss" scoped>
  @import '@/assets/css/variables.scss';

  #attribute-form {

    #header {
      border-bottom: 1px solid $grey-300;
      display: flex;
      justify-content: space-between;
      margin: 15px 15px 0px 15px;

      #header-text {
        @include Headline;
        display: block;
        margin: 20px;
        float: left;
      }

      #due-date-wrapper {
        overflow:hidden;
        width: 200px;
        float: left;
        margin-right: 20px;
      }
    }
  }

  #upload-new-file-dialog {
    margin: 16px auto;
    width: 51%;
  }
  .uploaded-files-list {
    width: 100%;
    display: flex;
    align-content: flex-start;
    flex-wrap: wrap;
  }
  .uploaded-file-item {
    width: 412px;
    height: 32px;
    background-color: $grey-100;
    margin: 5px 10px;
    padding: 2px 5px 2px 15px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .file-name {
    display: inline-block;
    margin-right: 15px;
    text-align: left;
  }
  .bytes {
    color: $grey-400;
    text-indent: 10px;
  }
  
  #start-workflow-main-section {
    min-height: 200px;
    overflow-y: auto;
    #index-field-list {
      margin: 15px;
    }
  }

  #footer {
    margin: 15px;
    display: flex;
    justify-content: flex-end;
  }

  @media (max-width: $tablet){
    #footer {
      flex-wrap: wrap;
    }
  }


  #upload-new-file-dialog {
    width: 480px;
  }
  
  .text-right {
    text-align: right;
  }
</style>
